import { useLayoutEffect, useState } from "react";
import { useApi } from "../hooks/useApi";
import { istEmpfaengerDauerhaftBlockiert, mailfehlerText } from "../lib/mailfehler-anzeige";
import { alsDatum, langesDatum, StatusSymbol } from "./AngefragteTageKarte";
import { Button, ErrorNote, Icon, Input, Modal, Checkbox, Textarea } from "./components";
import { formatDate } from "./format";

/**
 * „Verschieben“ — Terminvorschlag an den Gast (Antoine, 26.09.2026, wörtlich: „Vor Absenden der
 * Mail muss der Betreiber die Mail sehen, wie bei Versenden von Angebot und editieren können.
 * Zusätzlich muss er noch den Kalender sehen, wie im Angebots Builder, also welche Tage sind frei.
 * Das darf auch nicht heißen Absagen, sondern Verschieben.“).
 *
 * Löst den früheren `AblehnenMailDialog` (11.09.2026) ab. Anlass: der schickte einen festen
 * Textbaustein mit dem wörtlichen Platzhalter „[Datum einfügen]“ — und am 24.09. ging genau der an
 * einen echten Gast. Drei Dinge sind deshalb anders:
 *
 * 1. **Kein Platzhalter mehr.** Die Termine kommen aus dem Kalender: ein Klick auf einen oder
 *    mehrere Tage schreibt sie ausformuliert („Samstag, 12. Dezember 2026“) in den Text. Solange
 *    kein Tag gewählt ist, bleibt „Senden“ gesperrt. Der Server sperrt zusätzlich jede Lücke
 *    (`lib/freie-mail.ts`, `offenePlatzhalter`) — die Oberfläche ist nicht die einzige Wache.
 * 2. **Kein Storno.** Der Vorgang bleibt offen; im Verlauf steht „Terminvorschlag gesendet:
 *    <Daten>“ (`event.terminvorschlag_gesendet`). Echtes Stornieren bleibt „Vorgang stornieren“.
 * 3. **Vorschau wie beim Angebot.** Derselbe Aufbau wie `AngebotSendenDialog` (Kopf, Empfänger mit
 *    Kopie an mich, Betreff, Nachricht, Fuß mit „Vorschau“) — die Vorschau öffnet die Mail so, wie
 *    sie beim Gast ankommt (`POST /api/events/:id/offers/freetext-preview`, dieselbe Umwandlung und
 *    dasselbe Mandanten-Template wie der Versand).
 *
 * Kalender: dieselbe Quelle wie die Karte „Angefragter Tag“ im Angebots-Builder
 * (`GET /api/events/:id/angefragte-tage`, hier mit `wochen`/`von`), dieselben Statusfarben — nur
 * über mehrere Wochen und mit Auswahl.
 */

/** So viele Wochen zeigt der Kalender auf einmal; die Pfeile blättern um genau so viele. */
export const KALENDER_WOCHEN = 5;

interface Veranstaltung {
  id: string;
  titel: string;
  status: string;
}
interface KalenderTag {
  datum: string;
  eigenerTag: boolean;
  veranstaltungen?: Veranstaltung[];
}

export interface VerschiebenSendeErgebnis {
  emailSent: boolean;
  emailError: string | null;
}

/** `YYYY-MM-DD` des Montags der Woche, in der `iso` liegt (UTC, nie über die Zeitzone). */
export function montagVon(iso: string): string {
  const d = alsDatum(iso);
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return d.toISOString().slice(0, 10);
}

function plusTage(iso: string, tage: number): string {
  const d = alsDatum(iso);
  d.setUTCDate(d.getUTCDate() + tage);
  return d.toISOString().slice(0, 10);
}

/** Heute als `YYYY-MM-DD` in LOKALER Zeit — „heute“ ist der Kalendertag des Betreibers. */
function heuteIso(): string {
  const h = new Date();
  return `${h.getFullYear()}-${String(h.getMonth() + 1).padStart(2, "0")}-${String(h.getDate()).padStart(2, "0")}`;
}

/** Der Absatz mit den Terminvorschlägen — leer, solange nichts gewählt ist. */
export function termineAbsatz(termine: string[]): string {
  const sortiert = [...termine].sort();
  if (sortiert.length === 0) return "";
  if (sortiert.length === 1) {
    return `Gerne schlagen wir Ihnen einen Alternativtermin vor: ${langesDatum(sortiert[0]!)}. Würde Ihnen dieser Termin passen?`;
  }
  return [
    "Gerne schlagen wir Ihnen folgende Alternativtermine vor:",
    ...sortiert.map((t) => `– ${langesDatum(t)}`),
    "",
    "Welcher Termin würde Ihnen passen?",
  ].join("\n");
}

const SCHLUSS = "Wir freuen uns, von Ihnen zu hören.";

/**
 * Betreff und Text des Terminvorschlags. Anlass und Datum stehen nur drin, wenn es sie gibt —
 * nie eine Lücke, nie ein Platzhalter (der Fehler vom 24.09.).
 */
export function terminvorschlagMail(v: {
  anlass: string | null;
  datumIso: string | null;
  betrieb: string | null;
  termine: string[];
}): { subject: string; text: string } {
  const anlass = v.anlass?.trim() || null;
  const betrieb = v.betrieb?.trim() || null;
  const subject = `Terminvorschlag zu Ihrer Anfrage${anlass ? ` „${anlass}“` : ""}${betrieb ? ` bei ${betrieb}` : ""}`;
  const absatz = termineAbsatz(v.termine);
  const text = [
    "Sehr geehrte Damen und Herren,",
    "",
    `vielen Dank für Ihre Anfrage${anlass ? ` für „${anlass}“` : ""}${v.datumIso ? ` am ${langesDatum(v.datumIso)}` : ""}.`,
    "",
    `Leider können wir Ihnen ${v.datumIso ? "an diesem Tag" : "zum gewünschten Termin"} keinen Platz mehr anbieten.`,
    ...(absatz ? ["", absatz] : []),
    "",
    SCHLUSS,
    "",
    "Mit freundlichen Grüßen",
    ...(betrieb ? [betrieb] : []),
  ].join("\n");
  return { subject, text };
}

/**
 * Setzt die neuen Termine in einen Text, den der Betreiber schon bearbeitet hat: ersetzt den
 * zuletzt eingesetzten Absatz, sonst fügt er vor dem Schlusssatz ein. `null` = keine sichere
 * Stelle gefunden — dann bleibt der Text, und der Dialog sagt es.
 */
export function termineEinsetzen(text: string, alterAbsatz: string, neuerAbsatz: string): string | null {
  if (alterAbsatz && text.includes(alterAbsatz)) {
    if (neuerAbsatz) return text.replace(alterAbsatz, neuerAbsatz);
    return text.replace(`\n\n${alterAbsatz}`, "").replace(alterAbsatz, "");
  }
  if (!alterAbsatz && neuerAbsatz && text.includes(SCHLUSS)) return text.replace(SCHLUSS, `${neuerAbsatz}\n\n${SCHLUSS}`);
  return null;
}

/** Die Legende, einmal unter dem Kalender. Dieselben Status-Symbole wie der Wochenstreifen im Builder. */
const LEGENDE: { status?: string; text: string }[] = [
  { text: "frei" },
  { status: "inquiry", text: "Anfrage" },
  { status: "offer_sent", text: "Angebot offen" },
  { status: "offer_chosen", text: "gebucht" },
];

function Kalender({
  eventId,
  start,
  onStart,
  gewaehlt,
  onToggle,
}: {
  eventId: string;
  start: string;
  onStart: (iso: string) => void;
  gewaehlt: string[];
  onToggle: (iso: string) => void;
}) {
  const q = useApi<{ woche?: KalenderTag[] }>(`/api/events/${eventId}/angefragte-tage?wochen=${KALENDER_WOCHEN}&von=${start}`);
  const heute = heuteIso();
  const tage = q.data?.woche ?? [];
  const ende = plusTage(start, KALENDER_WOCHEN * 7 - 1);
  const zurueckMoeglich = start > montagVon(heute);
  const monat = (iso: string) => alsDatum(iso).toLocaleDateString("de-DE", { month: "long", year: "numeric", timeZone: "UTC" });
  return (
    <div className="rounded-xl border border-outline-variant/40 bg-surface-container-low p-3 dark:border-outline/30 dark:bg-surface-variant/10" data-testid="verschieben-kalender">
      <div className="mb-2 flex items-center gap-2">
        <span className="text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">Alternativtermine wählen</span>
        <span className="text-[11px] text-secondary-fixed-dim">· {monat(start) === monat(ende) ? monat(start) : `${monat(start)} – ${monat(ende)}`}</span>
        <span className="ml-auto flex gap-1">
          <Button type="button" variant="ghost" className="!h-[26px] !w-[26px] !p-0" aria-label="Frühere Wochen" disabled={!zurueckMoeglich} onClick={() => onStart(plusTage(start, -7 * KALENDER_WOCHEN))}>
            <Icon name="chevron_left" className="text-[18px]" />
          </Button>
          <Button type="button" variant="ghost" className="!h-[26px] !w-[26px] !p-0" aria-label="Spätere Wochen" onClick={() => onStart(plusTage(start, 7 * KALENDER_WOCHEN))}>
            <Icon name="chevron_right" className="text-[18px]" />
          </Button>
        </span>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"].map((w) => (
          <div key={w} className="pb-0.5 text-[10px] font-semibold uppercase tracking-[0.04em] text-on-surface-variant">
            {w}
          </div>
        ))}
        {q.loading && tage.length === 0
          ? Array.from({ length: KALENDER_WOCHEN * 7 }, (_, i) => <div key={i} className="h-[42px] animate-pulse rounded-[9px] bg-surface-container-high/40" />)
          : tage.map((t) => {
              const vorbei = t.datum < heute;
              const belegt = (t.veranstaltungen ?? []).length > 0;
              const an = gewaehlt.includes(t.datum);
              const gesperrt = vorbei || t.eigenerTag;
              const d = alsDatum(t.datum);
              const zahl = d.getUTCDate();
              const titel = t.eigenerTag
                ? `${langesDatum(t.datum)} — der angefragte Tag`
                : belegt
                  ? `${langesDatum(t.datum)}: ${(t.veranstaltungen ?? []).map((v) => v.titel).join(", ")}`
                  : `${langesDatum(t.datum)} — frei`;
              return (
                <button
                  key={t.datum}
                  type="button"
                  data-testid={`verschieben-tag-${t.datum}`}
                  aria-pressed={an}
                  disabled={gesperrt}
                  title={titel}
                  onClick={() => onToggle(t.datum)}
                  className={`relative h-[42px] rounded-[9px] pt-[5px] text-[13px] transition-colors disabled:cursor-not-allowed ${
                    an
                      ? "bg-primary font-bold text-on-primary"
                      : t.eigenerTag
                        ? "border border-dashed border-primary/60 text-primary"
                        : vorbei
                          ? "text-on-surface-variant/35"
                          : belegt
                            ? "bg-surface-container-lowest text-on-background hover:bg-surface-container-high dark:bg-surface-variant/10 dark:text-inverse-on-surface"
                            : "bg-[#F0EAE0] font-semibold text-secondary ring-1 ring-outline-variant hover:bg-[#F0EAE0] dark:bg-white/[0.08] dark:text-[#b8ae9e] dark:ring-white/10"
                  }`}
                >
                  {zahl === 1 && !an ? <span className="absolute left-1 top-0.5 text-[8.5px] font-bold uppercase text-on-surface-variant">{d.toLocaleDateString("de-DE", { month: "short", timeZone: "UTC" }).replace(".", "")}</span> : null}
                  {zahl}
                  <span className="flex h-[11px] items-center justify-center gap-[1px] pt-[2px]">
                    {!vorbei &&
                      (t.veranstaltungen ?? []).slice(0, 3).map((v) => (
                        <StatusSymbol key={v.id} status={v.status} className={an ? "text-on-primary/80" : "text-on-surface-variant"} />
                      ))}
                  </span>
                </button>
              );
            })}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10.5px] text-on-surface-variant">
        {LEGENDE.map((l) => (
          <span key={l.text} className="inline-flex items-center gap-1">
            {l.status ? <StatusSymbol status={l.status} /> : <span className="h-[8px] w-[8px] rounded-full bg-[#F0EAE0] ring-1 ring-outline-variant dark:bg-white/[0.08]" />} {l.text}
          </span>
        ))}
        <span className="inline-flex items-center gap-1">
          <span className="h-[8px] w-[8px] rounded-full border border-dashed border-primary" /> angefragter Tag
        </span>
      </div>
      {q.error && <p className="mt-1 text-[11px] text-error">Belegung konnte nicht geladen werden: {q.error}</p>}
    </div>
  );
}

export function VerschiebenDialog({
  open,
  onClose,
  eventId,
  anlass,
  eventDateIso,
  gaeste,
  betrieb,
  customerName,
  customerEmail,
  companyEmail,
  onSend,
  onPreview,
}: {
  open: boolean;
  onClose: () => void;
  eventId: string;
  anlass: string | null;
  eventDateIso: string | null;
  gaeste: number | null;
  betrieb: string | null;
  customerName: string | null;
  customerEmail: string | null;
  /** Wie `AngebotSendenDialog`: nur wenn gepflegt, zeigt die „Kopie an mich senden“-Checkbox. */
  companyEmail: string | null;
  /** Schickt die Mail (send-freetext mit `terminvorschlaege`). Storniert NICHTS. */
  onSend: (subject: string, text: string, ccSelf: boolean, termine: string[]) => Promise<VerschiebenSendeErgebnis>;
  /** Öffnet die Mail-Vorschau; wirft mit der exakten Server-Meldung, wenn sie abgelehnt wird. */
  onPreview: (subject: string, text: string) => Promise<void>;
}) {
  const startVorgabe = (): string => {
    const heute = heuteIso();
    return montagVon(eventDateIso && eventDateIso > heute ? eventDateIso : heute);
  };
  const [phase, setPhase] = useState<"compose" | "sending" | "done" | "mail_failed">("compose");
  const [termine, setTermine] = useState<string[]>([]);
  const [start, setStart] = useState(startVorgabe);
  const [subject, setSubject] = useState("");
  const [text, setText] = useState("");
  const [bearbeitet, setBearbeitet] = useState(false);
  const [termineNichtImText, setTermineNichtImText] = useState(false);
  const [ccSelf, setCcSelf] = useState(true);
  const [mailError, setMailError] = useState<string | null>(null);
  const [vorschauFehler, setVorschauFehler] = useState<string | null>(null);

  // Bei jedem Öffnen synchron zurücksetzen (wie `ErinnerungSendenDialog`) — kein Frame lang der
  // alte Stand eines anderen Vorgangs.
  useLayoutEffect(() => {
    if (open) {
      const m = terminvorschlagMail({ anlass, datumIso: eventDateIso, betrieb, termine: [] });
      setPhase("compose");
      setTermine([]);
      setStart(startVorgabe());
      setSubject(m.subject);
      setText(m.text);
      setBearbeitet(false);
      setTermineNichtImText(false);
      setMailError(null);
      setVorschauFehler(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, eventId]);

  function toggle(iso: string) {
    const neu = termine.includes(iso) ? termine.filter((t) => t !== iso) : [...termine, iso].sort();
    setTermine(neu);
    if (!bearbeitet) {
      setText(terminvorschlagMail({ anlass, datumIso: eventDateIso, betrieb, termine: neu }).text);
      return;
    }
    const eingesetzt = termineEinsetzen(text, termineAbsatz(termine), termineAbsatz(neu));
    if (eingesetzt === null) setTermineNichtImText(true);
    else {
      setText(eingesetzt);
      setTermineNichtImText(false);
    }
  }

  async function handleSend() {
    if (!subject.trim() || !text.trim() || termine.length === 0) return;
    setPhase("sending");
    const ergebnis = await onSend(subject.trim(), text.trim(), ccSelf, termine);
    if (!ergebnis.emailSent) {
      setMailError(ergebnis.emailError);
      setPhase("mail_failed");
      return;
    }
    setPhase("done");
  }

  async function handlePreview() {
    setVorschauFehler(null);
    try {
      await onPreview(subject.trim(), text.trim());
    } catch (e) {
      setVorschauFehler(e instanceof Error ? e.message : String(e));
    }
  }

  const sending = phase === "sending";
  const sperrGrund = !customerEmail
    ? "Ohne hinterlegte E-Mail kann keine Mail verschickt werden."
    : termine.length === 0
      ? "Bitte mindestens einen freien Tag im Kalender wählen."
      : !subject.trim() || !text.trim()
        ? "Betreff und Nachricht dürfen nicht leer sein."
        : null;

  return (
    <Modal
      open={open}
      onClose={() => !sending && onClose()}
      width={phase === "compose" || sending ? "640px" : "468px"}
      title={
        phase === "compose" || sending
          ? `Verschieben — Terminvorschlag an ${customerName ?? "Kunde"}`
          : phase === "mail_failed"
            ? "Mail NICHT versendet"
            : "Terminvorschlag gesendet"
      }
      subtitle={
        phase === "compose" || sending ? (
          <>
            {[anlass, eventDateIso ? `angefragt ${formatDate(eventDateIso)}` : "ohne Datum", gaeste ? `${gaeste} Gäste` : null].filter(Boolean).join(" · ")}
            {" · "}der Vorgang bleibt offen
          </>
        ) : undefined
      }
      footer={
        phase === "compose" || sending ? (
          <>
            {sperrGrund && <span className="mr-auto text-[11px] text-secondary-fixed-dim" data-testid="verschieben-sperrgrund">{sperrGrund}</span>}
            <Button type="button" variant="secondary" className={sperrGrund ? "" : "mr-auto"} onClick={() => void handlePreview()} disabled={sending || !subject.trim() || !text.trim()} data-testid="verschieben-vorschau">
              <Icon name="visibility" className="text-[16px]" /> Vorschau
            </Button>
            <Button type="button" variant="secondary" onClick={onClose} disabled={sending}>
              Abbrechen
            </Button>
            <Button type="button" data-testid="verschieben-senden" onClick={() => void handleSend()} disabled={sending || sperrGrund !== null}>
              <Icon name="send" className="text-[16px]" /> {sending ? "Sendet …" : "Terminvorschlag senden"}
            </Button>
          </>
        ) : phase === "mail_failed" ? (
          <>
            <Button type="button" variant="secondary" onClick={onClose}>
              Schließen
            </Button>
            {!istEmpfaengerDauerhaftBlockiert(mailError) && (
              <Button type="button" onClick={() => setPhase("compose")}>
                Zurück zur Nachricht
              </Button>
            )}
          </>
        ) : (
          <Button type="button" onClick={onClose}>
            Fertig
          </Button>
        )
      }
    >
      {phase === "compose" || sending ? (
          <div className="space-y-3.5">
            <div>
              <label className="mb-1 block text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">Empfänger</label>
              <div className="flex items-center gap-2.5 rounded-[10px] border border-outline-variant/60 bg-surface-container-lowest px-[11px] py-2 dark:border-outline/40">
                <span className="truncate text-[13px] font-semibold text-on-background dark:text-inverse-on-surface">{customerName ?? "Kunde"}</span>
                <span className="truncate text-[12px] text-secondary-fixed-dim">{customerEmail ?? "keine E-Mail hinterlegt"}</span>
              </div>
              {companyEmail && (
                <label className="mt-1.5 flex items-center gap-2 text-[12px] text-on-surface-variant">
                  <Checkbox checked={ccSelf} onChange={(e) => setCcSelf(e.target.checked)} disabled={sending} />
                  Kopie an mich senden ({companyEmail})
                </label>
              )}
            </div>

            <Kalender eventId={eventId} start={start} onStart={setStart} gewaehlt={termine} onToggle={toggle} />

            {termine.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5" data-testid="verschieben-gewaehlt">
                <span className="text-[11.5px] text-on-surface-variant">Vorgeschlagen:</span>
                {termine.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggle(t)}
                    className="inline-flex items-center gap-1 rounded-full bg-primary/12 px-2 py-0.5 text-[11.5px] font-semibold text-primary hover:bg-primary/20"
                    title="Termin entfernen"
                  >
                    {langesDatum(t)} <Icon name="close" className="text-[12px]" />
                  </button>
                ))}
              </div>
            )}

            <label className="block">
              <span className="mb-1 block text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">Betreff</span>
              <Input
                data-testid="verschieben-betreff"
                className="!py-2.5"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={300}
                disabled={sending}
              />
            </label>

            <label className="block">
              <span className="mb-1 block text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">Nachricht</span>
              {/* `Input` rendert ausschliesslich `<input>` — Mehrzeiligkeit waere verloren (s.
                  `design-komponenten-bestand.test.ts`, derselbe Grund wie im Vorgaenger-Dialog). */}
              <Textarea
                data-testid="verschieben-nachricht"
                className="min-h-[220px] text-[12.5px] leading-relaxed"
                value={text}
                onChange={(e) => {
                  setText(e.target.value);
                  setBearbeitet(true);
                }}
                maxLength={20_000}
                disabled={sending}
              />
              {termineNichtImText && (
                <p className="mt-1 text-[11px] font-semibold text-secondary dark:text-[#b8ae9e]" data-testid="verschieben-termine-selbst-anpassen">
                  Du hast den Text geändert — die gewählten Termine bitte selbst im Text anpassen.
                </p>
              )}
            </label>
            {vorschauFehler && <ErrorNote message={vorschauFehler} />}
          </div>
      ) : phase === "mail_failed" ? (
        <div className="space-y-3">
          <p className="text-[12.5px] text-secondary-fixed-dim">
            {customerName ?? "Die Kundin"} hat <strong>keine E-Mail</strong> erhalten. Am Vorgang hat sich nichts geändert.
          </p>
          <p className="w-full rounded-lg bg-error/10 px-3 py-2 text-left text-[11px] font-mono text-error dark:text-[#e39b92]">{mailfehlerText(mailError)}</p>
        </div>
      ) : (
        <p className="text-[12.5px] text-secondary-fixed-dim">
          {customerName ?? "Die Kundin"} hat {termine.length === 1 ? "den Vorschlag" : `${termine.length} Vorschläge`} per E-Mail erhalten. Der Vorgang bleibt offen.
        </p>
      )}
    </Modal>
  );
}
